import { addSeconds } from 'date-fns';
import { stringify as qsStringify } from 'qs';
import { getCookie, removeCookie, setCookie } from 'utils';

import { RefreshTokenResponse } from '@/modules/account/api';
import {
  COOKIE_ACCESS_TOKEN,
  COOKIE_REFRESH_TOKEN,
} from '@/modules/product/utils';

import { type FetchRequestInit } from './types';

export type FetchClient = typeof fetch;

const isServer = typeof window === 'undefined';

export function makeFetch(
  req: { host?: string | null; token?: string } = {}
): FetchClient {
  const globalFetch: FetchClient = fetch;

  const baseInit: Parameters<FetchClient>[1] = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    cache: 'no-store',
  };

  let baseUrl = process.env.NEXT_PUBLIC_API_URL;
  let accessToken = req?.token;
  if (isServer) {
    baseUrl = process.env.SSR_API_URL || '/';

    if (!req.host) {
      throw new Error('Host is not defined');
    }

    (baseInit.headers as Record<string, string>)['x-forwarded-host'] = req.host;
    (baseInit.headers as Record<string, string>)['x-forwarded-proto'] = 'https';
    (baseInit.headers as Record<string, string>)['x-forwarded-port'] = '443';
  } else {
    accessToken = getCookie(COOKIE_ACCESS_TOKEN) || undefined;
  }

  if (accessToken) {
    (baseInit?.headers as Record<string, string>).Authorization =
      `Bearer ${accessToken}`;
  }

  return function (
    input: Parameters<FetchClient>[0],
    init?: Parameters<FetchClient>[1]
  ): ReturnType<FetchClient> {
    const mergedInit: RequestInit = {
      ...baseInit,
      ...init,
      cache: 'no-store',
    };

    return globalFetch(`${baseUrl}${input}`, mergedInit);
  };
}

export async function storeClient<T>(
  path: string,
  init: FetchRequestInit = {},
  fetch?: FetchClient
): Promise<T> {
  if (!fetch) {
    if (isServer) {
      throw new Error('You must pass a fetch function from the server context');
    }

    fetch = makeFetch();
  }

  const queryParams = init?.queryParams
    ? `?${qsStringify(init.queryParams, {
        arrayFormat: 'brackets',
        encode: false,
        filter: (prefix, value) =>
          value !== undefined && value !== '' ? value : undefined,
      })}`
    : '';

  if (init?.queryParams) {
    delete init.queryParams;
  }

  const body = init?.body ? JSON.stringify(init.body) : null;

  if (init?.body) {
    delete init.body;
  }

  let response = await fetch(`${path}${queryParams}`, {
    ...init,
    body,
  }).catch((error) => {
    return error;
  });

  if (!response.ok && response.status > 399) {
    const details = await response.text();
    const originalRequestError =
      details && details !== '{}' ? details : 'Empty error';

    if (response.status === 401) {
      let refreshToken;
      if (isServer) {
        const { cookies } = await import('next/headers');
        refreshToken = cookies().get(COOKIE_REFRESH_TOKEN)?.value;
      } else {
        refreshToken = getCookie(COOKIE_REFRESH_TOKEN);
      }

      if (!refreshToken) {
        throw new Error(originalRequestError);
      }

      const refreshResponse = await fetch('/customer/refresh-token', {
        headers: { Authorization: '' },
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });

      if (!refreshResponse.ok && refreshResponse.status > 399) {
        if (!isServer) {
          removeCookie(COOKIE_REFRESH_TOKEN);
          removeCookie(COOKIE_ACCESS_TOKEN);
        }

        throw new Error(originalRequestError);
      }

      const {
        accessToken: newAccessToken,
        accessTokenExpiresIn,
        refreshToken: newRefreshToken,
        refreshTokenExpiresIn,
      }: RefreshTokenResponse = await refreshResponse.json();

      if (!isServer) {
        setCookie(COOKIE_ACCESS_TOKEN, newAccessToken, {
          expires: addSeconds(new Date(), accessTokenExpiresIn),
        });
        setCookie(COOKIE_REFRESH_TOKEN, newRefreshToken, {
          expires: addSeconds(new Date(), refreshTokenExpiresIn),
        });
      }

      response = await fetch(`${path}${queryParams}`, {
        ...init,
        headers: { ...init.headers, Authorization: `Bearer ${newAccessToken}` },
        body,
      }).catch((error) => error);

      if (!response.ok && response.status > 399) {
        throw new Error(originalRequestError);
      }
    } else {
      throw new Error(originalRequestError);
    }
  }

  return await response.json();
}
